import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "react-toastify";
import Slider from "@mui/material/Slider";
import TuneIcon from "@mui/icons-material/Tune";
import {
  fetchClientHealthConfig,
  updateClientHealthConfig,
} from "../../api/watchService";
import { vitalRangeColor } from "../../utils/vitalSeverity";

/**
 * react-app #560 — Caregiver-admin RR/HR bands for Xandar caregiver notify.
 *
 * Same ClientHealthConfig.customThresholds API as WatchThresholdSettings
 * (GMA-1588). Keys match cortex-be vitalsThresholdEngine / xandarNotifyGate:
 * respiratory_rate_* and heart_rate_*. Duration is stored as
 * xandar_notify_min_duration_minutes for a follow-on BE debounce (#1092).
 *
 * Non-admins see nothing. Vendor XK dashboard thresholds do not drive our
 * email/push — these CompassCare settings do.
 */

export const XANDAR_DURATION_KEY = "xandar_notify_min_duration_minutes";
export const XANDAR_DURATION_DEFAULT = 120;

/** Secondary text that clears WCAG AA on white (~4.5:1+). */
const MUTED = "#555";
const MUTED_SOFT = "#666";

export const XANDAR_VITAL_THRESHOLDS = [
  {
    metric: "respiratory_rate",
    label: "Respiratory rate",
    unit: "per minute",
    min: 4,
    max: 40,
    step: 1,
    required: true,
    bounds: [
      { bound: "warn_below", label: "Warning below", def: 10 },
      { bound: "warn_above", label: "Warning above", def: 24 },
      { bound: "crit_below", label: "Critical below", def: 8 },
      { bound: "crit_above", label: "Critical above", def: 28 },
    ],
  },
  {
    metric: "heart_rate",
    label: "Heart rate (optional)",
    unit: "BPM",
    min: 30,
    max: 200,
    step: 1,
    required: false,
    bounds: [
      { bound: "warn_below", label: "Warning below", def: 50 },
      { bound: "warn_above", label: "Warning above", def: 110 },
      { bound: "crit_below", label: "Critical below", def: 40 },
      { bound: "crit_above", label: "Critical above", def: 130 },
    ],
  },
];

export const thresholdKey = (metric, bound) => `${metric}_${bound}`;

const MANAGED_KEYS = new Set([
  ...XANDAR_VITAL_THRESHOLDS.flatMap((v) =>
    v.bounds.map((b) => thresholdKey(v.metric, b.bound)),
  ),
  XANDAR_DURATION_KEY,
]);

export const defaultThresholdValues = () => {
  const out = { [XANDAR_DURATION_KEY]: XANDAR_DURATION_DEFAULT };
  for (const v of XANDAR_VITAL_THRESHOLDS) {
    for (const b of v.bounds) out[thresholdKey(v.metric, b.bound)] = b.def;
  }
  return out;
};

export const buildThresholdValues = (custom = {}) => {
  const out = defaultThresholdValues();
  for (const key of MANAGED_KEYS) {
    const ov = custom?.[key];
    if (typeof ov === "number" && Number.isFinite(ov)) out[key] = ov;
  }
  return out;
};

export const mergeCustomThresholds = (existingCustom = {}, values = {}) => {
  const merged = { ...(existingCustom || {}) };
  for (const key of MANAGED_KEYS) delete merged[key];
  const defaults = defaultThresholdValues();
  for (const key of MANAGED_KEYS) {
    const val = values[key];
    if (typeof val === "number" && Number.isFinite(val) && val !== defaults[key]) {
      merged[key] = val;
    }
  }
  return merged;
};

/**
 * Enforce crit_below < warn_below < warn_above < crit_above per metric.
 * Returns null when ok, otherwise a caregiver-facing reason.
 */
export const validateThresholdOrdering = (values = {}) => {
  for (const v of XANDAR_VITAL_THRESHOLDS) {
    const cb = values[thresholdKey(v.metric, "crit_below")];
    const wb = values[thresholdKey(v.metric, "warn_below")];
    const wa = values[thresholdKey(v.metric, "warn_above")];
    const ca = values[thresholdKey(v.metric, "crit_above")];
    if (![cb, wb, wa, ca].every((n) => typeof n === "number" && Number.isFinite(n))) {
      continue;
    }
    if (!(cb < wb && wb < wa && wa < ca)) {
      return `${v.label}: keep critical below < warning below < warning above < critical above.`;
    }
  }
  return null;
};

export default function XandarThresholdSettings({
  clientId,
  canManage = false,
  onSaved,
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [values, setValues] = useState(() => defaultThresholdValues());
  const [dirty, setDirty] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const loadedFor = useRef(null);
  const isMounted = useRef(true);
  const activeClientId = useRef(clientId);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setLoadError(false);
    try {
      const config = await fetchClientHealthConfig(clientId);
      if (!isMounted.current || activeClientId.current !== clientId) return;
      const custom = config?.customThresholds || {};
      setValues(buildThresholdValues(custom));
      setDirty(false);
      loadedFor.current = clientId;
    } catch (err) {
      if (!isMounted.current || activeClientId.current !== clientId) return;
      console.error("Failed to load Xandar threshold config", err);
      setLoadError(true);
    } finally {
      if (isMounted.current && activeClientId.current === clientId) {
        setLoading(false);
      }
    }
  }, [clientId]);

  useEffect(() => {
    activeClientId.current = clientId;
    setOpen(false);
    setValues(defaultThresholdValues());
    setDirty(false);
    loadedFor.current = null;
  }, [clientId]);

  useEffect(() => {
    if (open && loadedFor.current !== clientId) load();
  }, [open, clientId, load]);

  const handleChange = (key, next) => {
    if (saving) return;
    setValues((prev) => ({ ...prev, [key]: next }));
    setDirty(true);
  };

  const handleSave = async () => {
    if (saving) return;
    const orderErr = validateThresholdOrdering(values);
    if (orderErr) {
      toast.error(orderErr);
      return;
    }
    const targetClientId = clientId;
    setSaving(true);
    try {
      // Re-fetch so a concurrent Watch save is not clobbered by a stale snapshot.
      const latest = await fetchClientHealthConfig(targetClientId);
      if (!isMounted.current || activeClientId.current !== targetClientId) return;
      const merged = mergeCustomThresholds(
        latest?.customThresholds || {},
        values,
      );
      const config = await updateClientHealthConfig(targetClientId, {
        customThresholds: merged,
      });
      if (!isMounted.current || activeClientId.current !== targetClientId) return;
      setValues(buildThresholdValues(config?.customThresholds || merged));
      setDirty(false);
      toast.success("Xandar alert thresholds saved");
      // The vitals panel is a SIBLING holding its own copy of these bands, so
      // without this the readings stay colored by the old thresholds until a
      // client reload (#581 review).
      onSaved?.();
    } catch (err) {
      if (!isMounted.current || activeClientId.current !== targetClientId) return;
      toast.error(err.message || "Failed to save Xandar thresholds");
    } finally {
      if (isMounted.current && activeClientId.current === targetClientId) {
        setSaving(false);
      }
    }
  };

  const handleReset = () => {
    if (saving) return;
    setValues(defaultThresholdValues());
    setDirty(true);
  };

  if (!canManage) return null;

  return (
    <div data-testid="xandar-threshold-settings" style={{ marginTop: "16px" }}>
      <button
        type="button"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: "6px",
          background: "none",
          border: "1px solid #C8E6C9",
          borderRadius: "6px",
          padding: "6px 10px",
          fontSize: "0.85rem",
          fontWeight: 600,
          color: "#388E3C",
          cursor: "pointer",
        }}
      >
        <TuneIcon style={{ fontSize: 16 }} />
        CompassCare alert thresholds {open ? "▲" : "▼"}
      </button>

      {open ? (
        <div
          style={{
            marginTop: "10px",
            backgroundColor: "#fff",
            border: "1px solid #C8E6C9",
            borderRadius: "8px",
            padding: "14px 16px",
          }}
        >
          <p style={{ fontSize: "0.8rem", color: MUTED, margin: "0 0 12px 0" }}>
            These bands control CompassCare email and push for this client.
            Xandar may still flag events on their dashboard using their own
            settings; caregiver notifications follow <strong>these</strong>{" "}
            values. Respiratory rate is primary; heart rate is optional.
          </p>

          {loadError ? (
            <p style={{ fontSize: "0.85rem", color: "#C62828", margin: 0 }}>
              Couldn&apos;t load saved thresholds.{" "}
              <button
                type="button"
                onClick={load}
                style={{
                  background: "none",
                  border: "none",
                  color: "#388E3C",
                  cursor: "pointer",
                  textDecoration: "underline",
                }}
              >
                Retry
              </button>
            </p>
          ) : loading ? (
            <p style={{ fontSize: "0.85rem", color: MUTED_SOFT, margin: 0 }}>
              Loading thresholds…
            </p>
          ) : (
            <>
              {XANDAR_VITAL_THRESHOLDS.map((v) => (
                <div key={v.metric} style={{ marginBottom: "18px" }}>
                  <div
                    style={{
                      fontSize: "0.85rem",
                      fontWeight: 700,
                      color: "#333",
                      marginBottom: "4px",
                    }}
                  >
                    {v.label}{" "}
                    <span style={{ color: MUTED_SOFT, fontWeight: 400 }}>
                      ({v.unit})
                    </span>
                  </div>
                  {v.bounds.map((b) => {
                    const key = thresholdKey(v.metric, b.bound);
                    const critical = b.bound.startsWith("crit");
                    const bandColor = vitalRangeColor(
                      critical ? "critical" : "caution",
                      { surface: "light" },
                    );
                    return (
                      <div
                        key={key}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "12px",
                          marginBottom: "4px",
                        }}
                      >
                        <span
                          style={{
                            width: "120px",
                            fontSize: "0.78rem",
                            color: bandColor,
                          }}
                        >
                          {b.label}
                        </span>
                        <Slider
                          data-testid={`xk-threshold-slider-${key}`}
                          value={values[key]}
                          min={v.min}
                          max={v.max}
                          step={v.step}
                          valueLabelDisplay="auto"
                          size="small"
                          disabled={saving}
                          onChange={(_e, next) =>
                            handleChange(
                              key,
                              Array.isArray(next) ? next[0] : next,
                            )
                          }
                          sx={{
                            flex: 1,
                            color: bandColor,
                          }}
                          aria-label={`${v.label} ${b.label}`}
                        />
                        <span
                          data-testid={`xk-threshold-value-${key}`}
                          style={{
                            width: "56px",
                            textAlign: "right",
                            fontSize: "0.85rem",
                            fontWeight: 600,
                            color: "#333",
                          }}
                        >
                          {values[key]}
                          {b.def !== values[key] ? (
                            <span style={{ color: MUTED_SOFT, fontWeight: 400 }}>
                              {" "}
                              *
                            </span>
                          ) : null}
                        </span>
                      </div>
                    );
                  })}
                </div>
              ))}

              <div style={{ marginBottom: "18px" }}>
                <div
                  style={{
                    fontSize: "0.85rem",
                    fontWeight: 700,
                    color: "#333",
                    marginBottom: "4px",
                  }}
                >
                  Preferred notify duration{" "}
                  <span style={{ color: MUTED_SOFT, fontWeight: 400 }}>
                    (minutes)
                  </span>
                </div>
                <p
                  style={{
                    fontSize: "0.75rem",
                    color: MUTED,
                    margin: "0 0 8px 0",
                  }}
                >
                  Saved now for when server debounce ships (issue #1092). Until
                  then, CompassCare notifies on the first out-of-band reading
                  regardless of this value. Default {XANDAR_DURATION_DEFAULT}{" "}
                  min (XK-aligned).
                </p>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "12px",
                  }}
                >
                  <Slider
                    data-testid={`xk-threshold-slider-${XANDAR_DURATION_KEY}`}
                    value={values[XANDAR_DURATION_KEY]}
                    min={5}
                    max={240}
                    step={5}
                    valueLabelDisplay="auto"
                    size="small"
                    disabled={saving}
                    onChange={(_e, next) =>
                      handleChange(
                        XANDAR_DURATION_KEY,
                        Array.isArray(next) ? next[0] : next,
                      )
                    }
                    sx={{ flex: 1, color: "#2E7D32" }}
                    aria-label="Xandar preferred notify duration minutes"
                  />
                  <span
                    data-testid={`xk-threshold-value-${XANDAR_DURATION_KEY}`}
                    style={{
                      width: "56px",
                      textAlign: "right",
                      fontSize: "0.85rem",
                      fontWeight: 600,
                    }}
                  >
                    {values[XANDAR_DURATION_KEY]}
                  </span>
                </div>
              </div>

              <div style={{ display: "flex", gap: "8px", marginTop: "4px" }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving || !dirty}
                  style={{
                    background: saving || !dirty ? "#A5D6A7" : "#2E7D32",
                    color: "#fff",
                    border: "none",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "0.85rem",
                    fontWeight: 600,
                    cursor: saving || !dirty ? "not-allowed" : "pointer",
                  }}
                >
                  {saving ? "Saving…" : "Save thresholds"}
                </button>
                <button
                  type="button"
                  onClick={handleReset}
                  disabled={saving}
                  style={{
                    background: "none",
                    border: "1px solid #BDBDBD",
                    borderRadius: "6px",
                    padding: "8px 16px",
                    fontSize: "0.85rem",
                    color: "#555",
                    cursor: saving ? "not-allowed" : "pointer",
                  }}
                >
                  Reset to defaults
                </button>
              </div>
              <p
                style={{
                  fontSize: "0.72rem",
                  color: MUTED_SOFT,
                  margin: "8px 0 0 0",
                }}
              >
                * differs from the default baseline.
              </p>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
}
